// MultiStepForm.js
import React, { useEffect, useState } from "react";
import { useRef } from "react";
import {
  Container,
  Form,
  Button,
  ProgressBar,
  Nav,
  Alert,
} from "react-bootstrap";
import SignaturePad from "react-signature-canvas"; // Import the SignaturePad component
import "./Signature.css";
import { getSasToken } from "../../store/thunk/user";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { updateForm } from "../../utils/updateForm";
import { uploadDocumentAndGetUrl } from "../../utils/uploadDocument";
import { dataURLtoBlob } from "../../utils/dataURLtoBlob";
import { triggerButtonClick } from "../../utils/triggerLibraryButton";
import GdprJobApplicationProcessingConsentFormDownload from "./Download/GdprJobApplicationProcessingConsentFormDownload";
import { Tabs, Tab, Box } from "@mui/material";
import moment from "moment";

const GdprJobApplicationProcessingConsentForm = ({
  currentDocument,
  documentList,
  user,
  sasToken,
  ipAddress,
  getSasToken,
}) => {
  const [step, setStep] = useState(1);
  const [value, setValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };
  const [progress, setProgress] = useState(0); // Progress percentage for each step
  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    surName: "",
    documentSignature: "",
    signedDate: "",
    consent: false,
    signature: "", // Store the signature data as a string
  });
  const [errors, setErrors] = useState({});
  const sigPadRef = useRef(null); // Reference to the SignaturePad component
  const documentSigPadRef = useRef(null); // Reference to the SignaturePad component
  useEffect(() => {
    setFormData(currentDocument);
    if (currentDocument?.step) {
      setStep(currentDocument?.step);
    }
    if (currentDocument?.progress) {
      setProgress(currentDocument?.progress);
    }
    if (currentDocument?.progress === 100) {
      getSasToken();
    }
  }, []);
  // Function to handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors({ ...errors, [name]: "" });
  };

  // Function to validate each step
  const validateStep = () => {
    let formErrors = {};
    if (step === 1) {
      if (!formData.title) formErrors.title = "This field is required";
      if (!formData.firstName) formErrors.firstName = "This field is required";
      if (!formData.surName) formErrors.surName = "This field is required";
      if (documentSigPadRef.current.isEmpty()) {
        formErrors.documentSignature = "This field is required"; // Check if the signature pad is empty
      }
    } else if (step === 2) {
      if (!formData.consent) formErrors.consent = "You must agree to the terms";
      if (sigPadRef.current.isEmpty())
        formErrors.signature = "Signature is required"; // Check if the signature pad is empty
    }
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Function to go to the next step
  const nextStep = async () => {
    if (validateStep()) {
      const documentSignatureImage = documentSigPadRef.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      const signatureBlob = dataURLtoBlob(documentSignatureImage);
      // Create a FormData object
      const formDataSign = new FormData();
      // Append the Blob as a file to the FormData
      // The second argument is the file name, which you can customize as needed
      formDataSign.append(
        "file",
        signatureBlob,
        `${[currentDocument["key"]]}.png`
      );
      // Send the FormData using postMultiPartFormData
      const documentUrl =
        (await uploadDocumentAndGetUrl(formDataSign, user?.token)) || "";
      if (documentUrl) {
        const payload = {
          [currentDocument["key"]]: {
            name: currentDocument?.name,
            ...formData,
            documentSignature: documentUrl,
            progress: progress + 50,
            step: step + 1,
          },
        };
        updateForm(payload, documentList, user);
        setStep(step + 1);
        setProgress(progress + 50);
      } else {
        toast.error(
          "Something went wrong while upload signature. please try again."
        );
      }
    }
  };

  // Function to go to the previous step
  const prevStep = () => {
    setStep(step - 1);
    setProgress(progress - 50);
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep()) {
      // Get the signature data as a base64 image
      const signatureImage = sigPadRef.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      setFormData({
        ...formData,
        signature: signatureImage,
      });
      const signatureBlob = dataURLtoBlob(signatureImage);
      // Create a FormData object
      const formDataSign = new FormData();
      // Append the Blob as a file to the FormData
      // The second argument is the file name, which you can customize as needed
      formDataSign.append(
        "file",
        signatureBlob,
        `${[currentDocument["key"]]}.png`
      );
      // Send the FormData using postMultiPartFormData
      const documentUrl =
        (await uploadDocumentAndGetUrl(formDataSign, user?.token)) || "";
      if (documentUrl) {
        const payload = {
          [currentDocument["key"]]: {
            name: currentDocument?.name,
            ...formData,
            progress: 100,
            step: step,
            signature: documentUrl,
            signDate: new Date(),
            ipAddress: ipAddress,
          },
        };
        updateForm(payload, documentList, user);
        toast.success("Successfully filled the form.");
        triggerButtonClick();
      } else {
        toast.error(
          "Something went wrong while upload signature. please try again."
        );
      }
      // Additional submit logic here, such as sending formData to the server
    }
  };
  // Clear the signature pad
  const clearSignature = () => {
    sigPadRef.current.clear();
    setFormData({ ...formData, signature: "" });
  };
  // Clear the signature pad
  const clearDocumentSignature = () => {
    documentSigPadRef.current.clear();
    setFormData({ ...formData, documentSignature: "" });
  };
  return (
    <Container className="mt-5">
      {currentDocument?.progress === 100 ? (
        <>
          <GdprJobApplicationProcessingConsentFormDownload
            data={currentDocument}
            sasToken={sasToken}
            user={documentList?.userDetail}
          />
          <Box sx={{ width: "100%" }}>
            <Tabs
              value={value}
              onChange={handleTabChange}
              aria-label="Personal Information"
              className="tab mt-2"
            >
              <Tab label="Personal Information" />
            </Tabs>
            {value === 0 && (
              <div className="container">
                <div className="row">
                  <h2>Personal Information</h2>
                  <ul class="list-group">
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Title</strong>{" "}
                      <span>{currentDocument?.title || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>First Name</strong>{" "}
                      <span>{currentDocument?.firstName || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Surname</strong>{" "}
                      <span>{currentDocument?.surName || "--"}</span>
                    </li>
                    <li
                      class="list-group-item d-flex"
                      style={{ textAlign: "justify" }}
                    >
                      <strong>
                        Rapid Resourcing Ltd General Data Protection
                        Regulations – Consent Form for Job Applicants We are
                        required to collect and hold data about all applicants
                        to enable the company to process all job applications.
                        GDPR places a further (and new) obligation on employers
                        to inform all applicants, in more detail why we collect
                        data, what we do with it, and how long we expect to
                        retain it. In order to process your application, we must
                        obtain your informed consent about the data that we may
                        hold about you. We are not planning to transfer your
                        data outside the EEA. We are required to hold personal
                        and special data (ethnic monitoring data) about you in
                        order to process your employment application. The types
                        of data, reason for holding the data and duration that
                        the data is stored can be found below. Type of data Why
                        we wish to hold it How long it will be kept for 1
                        Recruitment data Previous employers Types of job held at
                        other companies Previous salaries Skills and
                        qualifications obtained Criminal record (delete as
                        applicable) This will allow us to make a decision on
                        your suitability for employment/engagement Data obtained
                        during recruitment will only be kept until either your
                        application has been declined and then destroyed after 6
                        months if an offer of employment is made a more
                        comprehensive GDPR data processing form will be issued 2
                        Ethnic monitoring data Data relating to your racial
                        origin, religion, gender, sexual orientation, etc that
                        are classed as protected characteristics under the
                        Equality Act 2010 We use this data to understand the
                        ethnic make- up of our workforce and job applicants and
                        it allows us to inform our recruitment process if we
                        believe we do not have the correct diversity This data
                        will be kept for the duration of this round of
                        applications and will be anonymised and stored for 4
                        years afterwards. Agreement to use my data I hereby
                        freely give my prospective employer consent to use and
                        process my personal data relating to my job application
                        (examples of which are listed above). In giving my
                        consent: I understand that I can ask to see this data to
                        check its accuracy at any time via a subject access
                        request (SAR). I understand that I can ask for a copy of
                        my personal data held about me at any time, and this
                        request is free of charge. I understand that I can
                        request that data that is no longer required to be held,
                        can be removed from my file and destroyed. In this
                        instance I understand that I must provide details as to
                        why I believe that the data being held is incorrect or
                        being held unlawfully. I understand that if I am
                        unsuccessful with my application my data will be
                        destroyed after 6 months.
                        <br />
                        Applicant Name: {currentDocument?.firstName}{" "}
                        {currentDocument?.surName}
                      </strong>
                    </li>
                    <li className="list-group-item d-flex">
                      <div className="row">
                        <div className="col-12">
                          {currentDocument?.signature && (
                            <img
                              width={100}
                              src={`${currentDocument?.signature}?${sasToken}`}
                              alt="Signature"
                            />
                          )}
                        </div>
                      </div>
                    </li>
                    <li className="list-group-item d-flex">
                      Sign Date:{" "}
                      {moment(currentDocument?.signDate).format("DD-MM-YYYY")}
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </Box>
        </>
      ) : (
        <>
          <Nav variant="pills" className="justify-content-center mb-3">
            <Nav.Item>
              <Nav.Link
                className={step === 1 ? "text-light" : ""}
                active={step === 1}
              >
                Step 1
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 2 ? "text-light" : ""}
                active={step === 2}
              >
                Step 2
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <ProgressBar now={progress} className="mb-4" />

          <Form onSubmit={handleSubmit}>
            {step === 1 && (
              <div>
                <h3>Section 1: Personal Information</h3>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                    required
                  />
                  {errors.title && (
                    <Alert variant="danger">{errors.title}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    required
                  />
                  {errors.firstName && (
                    <Alert variant="danger">{errors.firstName}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Surname</Form.Label>
                  <Form.Control
                    type="text"
                    name="surName"
                    value={formData.surName}
                    onChange={handleChange}
                    isInvalid={!!errors.surName}
                    required
                  />
                  {errors.surName && (
                    <Alert variant="danger">{errors.surName}</Alert>
                  )}
                </Form.Group>
                <p>
                  Rapid Resourcing Ltd General Data Protection
                  Regulations – Consent Form for Job Applicants We are required
                  to collect and hold data about all applicants to enable the
                  company to process all job applications. GDPR places a further
                  (and new) obligation on employers to inform all applicants, in
                  more detail why we collect data, what we do with it, and how
                  long we expect to retain it. In order to process your
                  application, we must obtain your informed consent about the
                  data that we may hold about you. We are not planning to
                  transfer your data outside the EEA. We are required to hold
                  personal and special data (ethnic monitoring data) about you
                  in order to process your employment application. The types of
                  data, reason for holding the data and duration that the data
                  is stored can be found below. Type of data Why we wish to hold
                  it How long it will be kept for 1 Recruitment data Previous
                  employers Types of job held at other companies Previous
                  salaries Skills and qualifications obtained Criminal record
                  (delete as applicable) This will allow us to make a decision
                  on your suitability for employment/engagement Data obtained
                  during recruitment will only be kept until either your
                  application has been declined and then destroyed after 6
                  months if an offer of employment is made a more comprehensive
                  GDPR data processing form will be issued 2 Ethnic monitoring
                  data Data relating to your racial origin, religion, gender,
                  sexual orientation, etc that are classed as protected
                  characteristics under the Equality Act 2010 We use this data
                  to understand the ethnic make- up of our workforce and job
                  applicants and it allows us to inform our recruitment process
                  if we believe we do not have the correct diversity This data
                  will be kept for the duration of this round of applications
                  and will be anonymised and stored for 4 years afterwards.
                  Agreement to use my data I hereby freely give my prospective
                  employer consent to use and process my personal data relating
                  to my job application (examples of which are listed above). In
                  giving my consent: I understand that I can ask to see this
                  data to check its accuracy at any time via a subject access
                  request (SAR). I understand that I can ask for a copy of my
                  personal data held about me at any time, and this request is
                  free of charge. I understand that I can request that data that
                  is no longer required to be held, can be removed from my file
                  and destroyed. In this instance I understand that I must
                  provide details as to why I believe that the data being held
                  is incorrect or being held unlawfully. I understand that if I
                  am unsuccessful with my application my data will be destroyed
                  after 6 months. Applicant Name (Print):
                  <br />
                  Signature: ………………………………………………………
                  <br />
                  Date: …………………………………………………………………
                </p>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="date"
                    name="signedDate"
                    value={formData.signedDate}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Signed</Form.Label>
                  <div className="signature-pad">
                    <SignaturePad
                      ref={documentSigPadRef}
                      penColor="black"
                      canvasProps={{ className: "signature-canvas" }} // Customize the canvas appearance
                    />
                  </div>
                  {errors.documentSignature && (
                    <Alert variant="danger">{errors.documentSignature}</Alert>
                  )}
                  <Button
                    variant="secondary"
                    onClick={clearDocumentSignature}
                    className="mt-2"
                  >
                    Clear Signature
                  </Button>
                </Form.Group>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3>Step 2: Sign and Submit</h3>
                <p>
                  By signing this page, I confirm that I fully understand the
                  content and agree to adhere to it at all times. I also agree
                  that all the details provided in this document are accurate
                  and complete to the best of my knowledge.
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Signature</Form.Label>
                  <div className="signature-pad">
                    <SignaturePad
                      ref={sigPadRef}
                      penColor="black"
                      canvasProps={{ className: "signature-canvas" }} // Customize the canvas appearance
                    />
                  </div>
                  {errors.signature && (
                    <Alert variant="danger">{errors.signature}</Alert>
                  )}
                  <Button
                    variant="secondary"
                    onClick={clearSignature}
                    className="mt-2"
                  >
                    Clear Signature
                  </Button>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="consent"
                    label="Clear By ticking this box I consent to the company processing my
                data and the evidence of this document being stored within their
                system for the purpose of work finding services."
                    checked={formData.consent} // Use checked for checkboxes
                    onChange={handleChange}
                    isInvalid={!!errors.consent}
                  />
                  {errors.consent && (
                    <Alert variant="danger">{errors.consent}</Alert>
                  )}
                </Form.Group>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="success" type="submit">
                  Submit
                </Button>
              </div>
            )}
          </Form>
        </>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  currentDocument: state.user.currentDocument,
  documentList: state.user.documentList,
  user: state.user.loggedInUserData,
  sasToken: state.user.sasToken,
  ipAddress: state.user.ipAddress,
});
export default connect(mapStateToProps, { getSasToken })(
  GdprJobApplicationProcessingConsentForm
);
